import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { HelpCircle, Compass, RotateCcw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOnboarding } from './OnboardingProvider';
import { TOUR, tourForPath } from './tours';

/**
 * The way back into the training — a permanent ? in the topbar.
 *
 * A tour that can only ever be seen once is a tour everybody dismisses on their first
 * distracted morning and then asks a colleague about forever. This makes "show me that
 * again" a two-click operation from anywhere, and it is the reason `finish()` records
 * *skip* separately from *complete*: a skip is not a failure to be hidden, it is a user
 * saying "not now".
 *
 * The current screen's tour is offered first when there is one, because the person
 * clicking ? is almost always confused about the screen in front of them rather than
 * about the group's structure.
 */
export const HelpMenu = () => {
  const { t } = useTranslation('onboarding');
  const { pathname } = useLocation();
  const { replay, hasSeen } = useOnboarding();

  const screenTour = tourForPath(pathname);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={t('help.label')} data-tour="help">
          <HelpCircle className="size-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <span className="block font-medium text-foreground">{t('help.title')}</span>
          <span className="block text-caption text-muted-foreground">{t('help.body')}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {screenTour ? (
          <DropdownMenuItem onSelect={() => replay(screenTour.id)}>
            <Compass className="size-4" />
            <span className="flex-1">{t('help.thisScreen')}</span>
            {hasSeen(screenTour.id) ? <Check className="size-3.5 text-muted-foreground" /> : null}
          </DropdownMenuItem>
        ) : null}

        <DropdownMenuItem onSelect={() => replay(TOUR.WELCOME)}>
          <RotateCcw className="size-4 rtl-flip" />
          <span className="flex-1">{t('help.replayWelcome')}</span>
          {hasSeen(TOUR.WELCOME) ? <Check className="size-3.5 text-muted-foreground" /> : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HelpMenu;
